/**
 * The coven ring (coven design 2026-10-26, `Claude outputs/coven-design-2026-10-26/`):
 * 13 seats around a dotted ring, the Outer Circle under it, the full-coven
 * swap. Pure: the seats, their places, and every string from DESIGN.md §3.
 *
 * Seats keep their place: a step back leaves the candle out where they sat,
 * and the next add takes the first empty seat. After a reload the seats are
 * ordered by `coven_since` (the server's seat time). An account already over
 * 13 lights the 13 earliest and shows "+N".
 *
 * No pronouns anywhere, and no money.
 */
export const COVEN_MAX = 13

const since = (f) => String((f && f.coven_since) || '')

export const covenOf = (friends = []) => (friends || []).filter((f) => f && f.tier === 'coven')
export const outerOf = (friends = []) => (friends || []).filter((f) => f && f.tier !== 'coven')
export const initialOf = (name = '') => (String(name || '').trim().charAt(0) || '?').toUpperCase()

/**
 * The 13 seats: `order` (the seat ids as the ring last drew them) keeps
 * everyone where they sat; anyone new takes the first empty seat, earliest
 * first. Returns { seats: [friend|null x13], overflow, count }.
 */
export function seatsFrom (friends = [], { order = null, max = COVEN_MAX } = {}) {
  const coven = covenOf(friends).slice().sort((a, b) => since(a).localeCompare(since(b)))
  const byId = new Map(coven.map((f) => [f.querent_uuid, f]))
  const seats = Array.from({ length: max }, () => null)
  const placed = new Set()
  if (Array.isArray(order)) {
    order.slice(0, max).forEach((id, i) => {
      if (id && byId.has(id) && !placed.has(id)) { seats[i] = byId.get(id); placed.add(id) }
    })
  }
  for (const f of coven) {
    if (placed.has(f.querent_uuid)) continue
    const i = seats.indexOf(null)
    if (i < 0) break
    seats[i] = f
    placed.add(f.querent_uuid)
  }
  return { seats, overflow: Math.max(0, coven.length - max), count: coven.length }
}

export const firstEmpty = (seats = []) => seats.indexOf(null)

/** Seat i's centre on the ring (41% radius, seat 1 at the top). */
export function seatPos (i, max = COVEN_MAX) {
  const a = (-90 + (i * 360) / max) * Math.PI / 180
  return { left: `${(50 + 41 * Math.cos(a)).toFixed(2)}%`, top: `${(50 + 41 * Math.sin(a)).toFixed(2)}%` }
}

/** The heading and its line, by how many are in (DESIGN §3). */
export function headCopy (n) {
  const shown = Math.min(n, COVEN_MAX)
  const count = `${shown} of ${COVEN_MAX}`
  if (n <= 0) {
    return { count, lede: 'Your coven is up to 13 people who see ', em: 'your real readings',
      after: ' when you share with them. Everyone else sees the lesson.', small: 'Tap a candle to choose who.' }
  }
  if (n >= COVEN_MAX) {
    return { count, lede: 'A full circle. These 13 see your real readings.', em: '', after: '',
      small: 'To bring someone new in, someone steps back to your Outer Circle.' }
  }
  return { count, lede: 'Your coven sees ', em: 'your real readings', after: '. Everyone else sees the lesson.', small: '' }
}

export const centerSub = (n) => (n <= 0 ? 'seats lit' : n >= COVEN_MAX ? 'full circle' : '')

export const swapButton = (out, incoming) => (out ? `${out} steps back · ${incoming} joins` : 'Choose who steps back')

export const toasts = {
  full: (name) => `${name} is in. Your coven is a full circle.`,
  back: (name) => `${name} is back in your Outer Circle.`,
  swap: (incoming, out) => `${incoming} joins. ${out} is in your Outer Circle.`,
}

export const COPY = {
  outerHint: "Everyone you're connected with. They see your lessons, not your readings.",
  outerEmpty: "Your Outer Circle is everyone you've connected with. Find seekers in the Community to add friends.",
  sheetEmptyTitle: 'Who joins your coven?',
  sheetEmptyFine: "They'll see your real readings when you share with your coven.",
  sheetEmptyNone: 'Your Outer Circle is empty. Find seekers in the Community to add friends.',
  swapTitle: 'Your coven is full.',
  swapAsk: 'Who should step back to your Outer Circle?',
  swapFine: (incoming) => `Tap a face. ${incoming} takes that seat. Whoever steps back isn't told, and sees your lessons instead of your readings.`,
  notNow: 'Not now',
  stepBack: 'Step back to Outer Circle',
  inCoven: (mutual) => `In your coven${mutual ? ' · and you’re in theirs' : ''}`,
  viewMirror: (name) => `View ${name}’s Mirror`,
  quiet: (name) => `Stepping back is quiet: ${name} isn't told, and sees your lessons instead of your readings from now on.`,
  waiting: (name) => `${name} is waiting`,
  choosing: 'choosing a seat…',
  addedYou: 'added you to theirs',
  add: (theyAdded) => (theyAdded ? 'Add back' : 'Add to coven'),
  seatEmpty: (i) => `Empty seat ${i + 1}. Choose someone for your coven`,
  seatFilled: (name) => `${name}, in your coven`,
  seatPick: (name) => `${name}. Choose to step back`,
  ring: (n) => `Your coven, ${Math.min(n, COVEN_MAX)} of ${COVEN_MAX} seats`,
}

/** The gift room's one ask after the reply (2026-10-26, circle consent). */
export function circleAskText (name = '') {
  const who = String(name || '').trim() || 'them'
  return { line: `Add ${who} to your circle?`, yes: `Add ${who}`, no: 'Not now', added: `${who} is in your circle now.` }
}

/** The ask as a room message, only while the server says it is owed. */
export function circleAskMessage (ask, inv) {
  if (!ask || !ask.name || !inv) return null
  return { text: '', type: 'reader', _context: 'circle-ask', circle_ask: { name: ask.name, inv } }
}

/** The sent page and Journal: a gift join no longer joins anyone's circle. */
export const timelineJoinedTitle = () => 'Joined Mystic AIda'
